import { useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { DownloadOutlined } from '@ant-design/icons';
import { Button, Column, DataTable } from './ui';

export interface AutoRenewExpiringRow {
  domain: string;
  accountId?: string;
  expiresAt?: string;
  daysLeft?: number;
}

type SortKey = 'domain' | 'expiresAt' | 'daysLeft' | 'accountId';
type SortDir = 'asc' | 'desc';

interface Props {
  rows: AutoRenewExpiringRow[];
}

export default function AutoRenewExpiringTable({ rows }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>('daysLeft');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return va - vb;
      return String(va).localeCompare(String(vb));
    });
    if (sortDir === 'desc') copy.reverse();
    return copy;
  }, [rows, sortKey, sortDir]);

  function toggle(key: SortKey) {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'daysLeft' ? 'asc' : 'asc');
    }
  }

  function dirFor(key: SortKey): SortDir | undefined {
    return sortKey === key ? sortDir : undefined;
  }

  function downloadCsv() {
    const header = ['domain', 'account', 'expires_at', 'days_left'];
    const lines = [header.join(',')];
    for (const r of sorted) {
      lines.push(
        [
          csvCell(r.domain),
          csvCell(r.accountId || ''),
          csvCell(r.expiresAt || ''),
          csvCell(r.daysLeft != null ? String(r.daysLeft) : ''),
        ].join(','),
      );
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `autorenew-expiring-${moment().format('YYYYMMDD-HHmm')}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  const columns: Column<AutoRenewExpiringRow>[] = [
    {
      key: 'domain',
      title: 'Domain',
      sortable: true,
      sortDirection: dirFor('domain'),
      onSort: () => toggle('domain'),
      render: (r) => <span className="font-mono text-[12px] text-text">{r.domain}</span>,
    },
    {
      key: 'accountId',
      title: 'Account',
      sortable: true,
      sortDirection: dirFor('accountId'),
      onSort: () => toggle('accountId'),
      render: (r) => <span className="text-text-2 text-[12px]">{r.accountId || '—'}</span>,
    },
    {
      key: 'expiresAt',
      title: 'Expires',
      sortable: true,
      sortDirection: dirFor('expiresAt'),
      onSort: () => toggle('expiresAt'),
      render: (r) => (
        <span className="font-mono text-[12px] text-text-2 tabular-nums">
          {r.expiresAt ? moment(r.expiresAt).format('YYYY-MM-DD') : '—'}
        </span>
      ),
    },
    {
      key: 'daysLeft',
      title: 'Days Left',
      align: 'right',
      sortable: true,
      sortDirection: dirFor('daysLeft'),
      onSort: () => toggle('daysLeft'),
      render: (r) => (
        <span className="font-mono text-[12px] tabular-nums text-text">
          {r.daysLeft != null ? r.daysLeft : '—'}
        </span>
      ),
    },
  ];

  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-2xs text-text-3">
          {rows.length} domain{rows.length === 1 ? '' : 's'}
        </span>
        <Button size="sm" variant="ghost" iconLeft={<DownloadOutlined />} onClick={downloadCsv}>
          CSV
        </Button>
      </div>
      <div className="max-h-[220px] overflow-y-auto border border-border rounded-md">
        <DataTable columns={columns} data={sorted} rowKey={(r) => r.domain} />
      </div>
    </div>
  );
}

function csvCell(value: string): string {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}
